import { useState } from "react";
import {
  Search,
  Plus,
  Briefcase,
  FileText,
  IndianRupee,
  TrendingUp,
  Users,
  Eye,
  PieChart,
  Upload,
} from "lucide-react";
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { AddContractorDialog } from "./AddContractorDialog";
import LoadingScreen from "components/UI_components/LoadingScreen";
import NoDataIcon from "assets/empty state/NoItems.svg";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

export function ContractorDashboard({
  contractors,
  onSelectContractor,
  onAddContractor,
  onOpenCreateWorkOrder,
  onImportContractor,
  loading,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const totalContractors = contractors.length;
  const totalWorkOrders = contractors.reduce((sum, c) => sum + c.workOrders, 0);
  const totalWorkOrderValue = contractors.reduce(
    (sum, c) => sum + c.totalWorkOrderValue,
    0,
  );
  const totalPaymentDone = contractors.reduce((sum, c) => sum + c.totalPaid, 0);
  const totalDue = contractors.reduce((sum, c) => sum + c.totalDue, 0);

  const filteredContractors = contractors
    .filter(
      (c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.category.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "value")
        return b.totalWorkOrderValue - a.totalWorkOrderValue;
      if (sortBy === "due") return b.totalDue - a.totalDue;
      return 0;
    });

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount?.toLocaleString()}`;
  };

  const categoryData = [
    {
      name: "Civil",
      value: contractors.filter((c) => c.category === "Civil").length,
      color: "#3B0069",
    }, // sm-primary
    {
      name: "Electrical",
      value: contractors.filter((c) => c.category === "Electrical").length,
      color: "#8067E0",
    }, // sm-secondary
    {
      name: "Interior",
      value: contractors.filter((c) => c.category === "Interior").length,
      color: "#FF7C01",
    }, // sm-accent
    {
      name: "Other",
      value: contractors.filter(
        (c) => !["Civil", "Electrical", "Interior"].includes(c.category),
      ).length,
      color: "#51079fd4",
    }, // sm-hover
  ].filter((d) => d.value > 0);

  const topContractors = [...contractors]
    .sort((a, b) => b.totalWorkOrderValue - a.totalWorkOrderValue)
    .slice(0, 5)
    .map((c) => ({
      name: c.name.substring(0, 12),
      value: c.totalWorkOrderValue / 100000,
    }));

  // Components
  const Button = ({
    variant = "default",
    size = "default",
    className = "",
    children,
    ...props
  }) => {
    const baseStyles =
      "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
    const variants = {
      default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
      destructive:
        "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
      outline:
        "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-sm-accent-foreground",
      secondary:
        "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
      ghost: "hover:bg-accent hover:text-sm-accent-foreground",
      link: "text-sm-primary underline-offset-4 hover:underline",
    };
    const sizes = {
      default: "h-9 px-4 py-2",
      sm: "h-8 rounded-md px-3 text-xs",
      lg: "h-10 rounded-md px-8",
      icon: "h-9 w-9",
    };
    return (
      <button
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {children}
      </button>
    );
  };

  const Card = ({ className = "", children }) => (
    <div
      className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );

  const CardContent = ({ className = "", children }) => (
    <div className={`p-2 ${className}`}>{children}</div>
  );

  const CardHeader = ({ className = "", children }) => (
    <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>
      {children}
    </div>
  );

  const CardTitle = ({ className = "", children }) => (
    <h3
      className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
    >
      {children}
    </h3>
  );

  const Badge = ({ variant = "default", className = "", children }) => {
    const baseStyles =
      "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const variants = {
      default:
        "border-transparent bg-sm-primary text-white hover:bg-sm-primary/80",
      secondary: "border-transparent bg-sm-secondary/10 text-sm-primary",
      destructive: "border-transparent bg-red-100 text-red-900",
      outline: "text-foreground",
    };
    return (
      <div className={cn(baseStyles, variants[variant], className)}>
        {children}
      </div>
    );
  };

  const Input = ({ className = "", ...props }) => (
    <input
      className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  );

  const Label = ({ children, className = "" }) => (
    <label
      className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}
    >
      {children}
    </label>
  );

  return (
    <div className="w-full flex-1">
      <LoadingScreen loading={loading}>
        <div className="w-full px-10 py-8 flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                Contractor Management
              </h1>
              <p className="text-sm text-muted-foreground">
                Manage contractors, work orders, and payments
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="h-9 text-sm gap-2 border-border/60"
                onClick={onImportContractor}
              >
                <Upload className="h-4 w-4" />
                Import From Excel
              </Button>
              <Button
                variant="outline"
                className="h-9 text-sm gap-2 border-border/60"
                onClick={() => onOpenCreateWorkOrder?.()}
              >
                <Plus className="h-4 w-4" />
                Work Order
              </Button>

              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-sm-primary hover:bg-sm-primary/90 text-sm-primary-foreground h-9 text-sm gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Contractor
              </Button>

              {/* Add Contractor Dialog */}
              <AddContractorDialog
                open={isAddDialogOpen}
                onOpenChange={setIsAddDialogOpen}
                onAddContractor={onAddContractor}
              />
            </div>
          </div>

          {/* Analytics Cards */}
          <div className="grid grid-cols-5 gap-4">
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-sm-secondary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-sm-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {totalContractors}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Total Contractors
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-sm-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-sm-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {totalWorkOrders}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Total Work Orders
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-sm-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-sm-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {formatCurrency(totalWorkOrderValue)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Work Order Value
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {formatCurrency(totalPaymentDone)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Paid</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {formatCurrency(totalDue)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Due</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          {/* <div className="grid grid-cols-3 gap-4">
        <Card className="border-border/60 shadow-none">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <PieChart className="h-4 w-4 text-sm-secondary" />
              Contractor Categories
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex items-center gap-4">
              <ResponsiveContainer width={90} height={90}>
                <RechartsPie>
                  <Pie
                    data={categoryData}
                    innerRadius={28}
                    outerRadius={42}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </RechartsPie>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {categoryData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-muted-foreground">{item.name}</span>
                    </div>
                    <span className="text-sm font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-none col-span-2">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-sm-secondary" />
              Top 5 Contractors by Value (in Lakhs)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={90}>
              <BarChart data={topContractors}>
                <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip
                  contentStyle={{ fontSize: 11, padding: "6px 10px", borderRadius: "6px" }}
                  formatter={(value) => [`₹${value.toFixed(2)}L`, "Value"]}
                />
                <Bar dataKey="value" fill="#8067E0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div> */}

          {/* Filters Row */}
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">
              {filteredContractors.length} contractors
            </span>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  placeholder="Search contractors..."
                  className="h-9 text-sm pl-8 rounded-lg w-52 border border-black/60"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select
                className="w-[140px] h-9 text-sm border border-input rounded-md px-3 py-1 bg-background text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="" disabled>
                  Sort by
                </option>
                <option value="name">Name</option>
                <option value="value">Work Order Value</option>
                <option value="due">Due Amount</option>
              </select>
            </div>
          </div>

          {/* Contractor Table */}
          <Card className="border-border/60 shadow-none flex-1">
            <CardContent className="p-0">
              {filteredContractors.length > 0 ? (
                <table className="w-full caption-bottom text-sm">
                  <thead className="[&_tr]:border-b">
                    <tr className="border-b transition-colors hover:bg-transparent border-border/60">
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                        Contractor Name
                      </th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                        Category
                      </th>
                      <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-xs text-center">
                        Projects
                      </th>
                      <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-xs text-center">
                        Work Orders
                      </th>
                      <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                        Total Paid
                      </th>
                      <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                        Total Due
                      </th>
                      <th className="h-10 px-4 w-[14%] align-middle font-medium text-muted-foreground text-xs text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {filteredContractors.map((contractor) => (
                      <tr
                        key={contractor.id}
                        className="group cursor-pointer hover:bg-muted/30 border-b border-border/40 transition-colors"
                        onClick={() => onSelectContractor(contractor)}
                      >
                        <td className="p-4 align-middle py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-sm-secondary/10 flex items-center justify-center">
                              <Briefcase className="h-4 w-4 text-sm-secondary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {contractor.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {contractor.contact}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 align-middle">
                          <Badge
                            variant="secondary"
                            className="text-xs font-normal bg-sm-secondary/10 text-sm-primary border-0"
                          >
                            {contractor.category}
                          </Badge>
                        </td>
                        <td className="p-4 align-middle text-center text-sm">
                          {contractor.projects}
                        </td>
                        <td className="p-4 align-middle text-center text-sm">
                          {contractor.workOrders}
                        </td>
                        <td className="p-4 align-middle text-right">
                          <span className="text-sm font-medium text-green-600">
                            {formatCurrency(contractor.totalPaid)}
                          </span>
                        </td>
                        <td className="p-4 align-middle text-right">
                          <span className="text-sm font-medium text-red-600">
                            {formatCurrency(contractor.totalDue)}
                          </span>
                        </td>
                        <td className="align-middle text-center">
                          <button
                            size="sm"
                            className="px-2 py-2 items-center text-xs gap-1.5 hidden group-hover:flex flex-row rounded-lg transition-opacity hover:bg-sm-accent hover:text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectContractor(contractor);
                            }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View Profile
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <img
                    src={NoDataIcon}
                    alt="No Contractors"
                    className="mb-4"
                    width={200}
                  />
                  <span className="text-muted-foreground text-lg">
                    No Contractors Found
                  </span>
                  <p className="text-muted-foreground text-sm">
                    Add a contractor to start managing your work orders.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </LoadingScreen>
    </div>
  );
}
